import React, { useState } from 'react';
import { ArrowUpRight, Mail, MapPin, MessageCircle, Calendar, Clock } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { MouseGlow } from '@/components/site/MouseGlow';
import { WordReveal } from '@/components/site/WordReveal';
import { Reveal } from '@/components/site/Reveal';
import { SectionLabel } from '@/components/site/SectionLabel';
import { MagneticButton } from '@/components/site/MagneticButton';
import { SITE } from '@/data/site';

const BUDGETS = ['< ₹5L', '₹5–15L', '₹15–35L', '₹35L+', 'Not sure yet'];

// Swap this for your Calendly / Cal.com / Savvycal embed URL.
// e.g. https://cal.com/niodelta/discovery or https://calendly.com/niodelta/30min?embed_domain=...
const CALENDAR_EMBED_URL = 'https://cal.com/niodelta/discovery?embed=true&theme=dark';

export default function Contact() {
  const [form, setForm] = useState({
    name: '', email: '', company: '', budget: BUDGETS[1], message: '',
  });
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('A name, email and a few lines is all we need.');
      return;
    }
    const text =
      `Hi NioDelta — ${form.name} here${form.company ? ` from ${form.company}` : ''}.%0A` +
      `Budget: ${form.budget}.%0A%0A` +
      `${form.message}%0A%0A` +
      `Reply to: ${form.email}`;
    window.open(`${SITE.whatsapp}?text=${text}`, '_blank', 'noopener');
    setSent(true);
    toast.success('Opening WhatsApp with your brief…');
  };

  return (
    <div data-testid="contact-page">
      <Toaster theme="dark" position="bottom-right" />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <MouseGlow intensity={0.26} />
        <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-28 md:pt-36 pb-16 md:pb-20">
          <SectionLabel num="01" data-testid="contact-eyebrow">
            Contact — A short note is enough
          </SectionLabel>
          <WordReveal
            as="h1"
            text="Tell us what you’re building. We reply within a business day."
            accent="business"
            data-testid="contact-headline"
            className="mt-8 text-4xl sm:text-5xl md:text-6xl lg:text-[72px] font-extrabold tracking-tightest leading-[1.03] text-balance max-w-5xl"
          />
          <Reveal delay={420}>
            <p className="mt-10 text-lg md:text-xl text-ink-muted max-w-2xl leading-relaxed">
              No SDR, no auto-reply, no form-to-nowhere. The note lands with a partner,
              and one of us writes back—personally—within one working day. Promise.
            </p>
          </Reveal>
          <Reveal delay={560}>
            <div className="mt-8 inline-flex items-center gap-2.5 text-xs uppercase tracking-[0.24em] text-ink-muted">
              <Clock size={14} className="text-violet" />
              <span><span className="text-white">Reply</span> within 1 business day · IST 9–7</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FORM + SIDEBAR */}
      <section className="relative border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Form (with signature animated glow) */}
          <div className="md:col-span-7 relative">
            {/* Signature: subtle animated gradient glow behind the form */}
            <div aria-hidden className="pointer-events-none absolute -inset-6 md:-inset-10">
              <div
                data-testid="contact-form-glow"
                className="absolute inset-0 opacity-80 blur-3xl contact-glow"
                style={{
                  background:
                    'radial-gradient(45% 55% at 25% 30%, rgba(108,92,231,0.45) 0%, transparent 70%),' +
                    'radial-gradient(40% 50% at 80% 80%, rgba(75,159,255,0.32) 0%, transparent 70%),' +
                    'radial-gradient(35% 45% at 60% 20%, rgba(108,92,231,0.25) 0%, transparent 70%)',
                }}
              />
            </div>

            <Reveal>
              <form
                data-testid="contact-form"
                onSubmit={submit}
                className="relative rounded-[20px] border border-white/[0.12] bg-ink-surface/90 backdrop-blur-sm p-7 md:p-10"
              >
                <div className="flex items-baseline justify-between mb-8">
                  <div className="text-[11px] uppercase tracking-[0.28em] text-ink-muted">
                    — Project brief
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-ink-muted">
                    5 fields · 90 seconds
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Your name">
                    <input
                      data-testid="contact-name"
                      required
                      value={form.name}
                      onChange={update('name')}
                      placeholder="Asha Krishnan"
                      className="input"
                      autoComplete="name"
                    />
                  </Field>
                  <Field label="Email">
                    <input
                      data-testid="contact-email"
                      required
                      type="email"
                      value={form.email}
                      onChange={update('email')}
                      placeholder="asha@company.in"
                      className="input"
                      autoComplete="email"
                    />
                  </Field>
                  <Field label="Company (optional)" className="md:col-span-2">
                    <input
                      data-testid="contact-company"
                      value={form.company}
                      onChange={update('company')}
                      placeholder="What are you building?"
                      className="input"
                      autoComplete="organization"
                    />
                  </Field>

                  <Field label="Budget range" className="md:col-span-2">
                    <div className="flex flex-wrap gap-2 pt-1">
                      {BUDGETS.map((b) => (
                        <button
                          key={b}
                          type="button"
                          data-testid={`contact-budget-${b.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                          onClick={() => setForm((s) => ({ ...s, budget: b }))}
                          className={`px-4 py-2 rounded-full border text-sm transition-colors ${
                            form.budget === b
                              ? 'border-violet bg-violet/15 text-white'
                              : 'border-white/[0.12] text-ink-muted hover:text-white hover:border-white/25'
                          }`}
                        >
                          {b}
                        </button>
                      ))}
                    </div>
                  </Field>

                  <Field label="A few lines about the project" className="md:col-span-2">
                    <textarea
                      data-testid="contact-message"
                      required
                      rows={6}
                      value={form.message}
                      onChange={update('message')}
                      placeholder="Audience, the rough goal, anything that already exists. Plain words are fine."
                      className="input resize-none"
                    />
                  </Field>
                </div>

                <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                  <p className="text-xs text-ink-muted max-w-sm leading-relaxed">
                    Hitting send opens WhatsApp with your brief drafted—you can edit before sending,
                    or copy and email it instead.
                  </p>
                  <MagneticButton
                    variant="primary"
                    data-testid="contact-submit"
                    onClick={submit}
                  >
                    {sent ? 'Sent — reopen WhatsApp' : 'Send brief'}
                    <ArrowUpRight size={15} />
                  </MagneticButton>
                </div>
              </form>
            </Reveal>
          </div>

          {/* Sidebar */}
          <Reveal className="md:col-span-5 md:pl-6 md:border-l border-white/10" delay={120}>
            <div className="text-[11px] uppercase tracking-[0.28em] text-ink-muted mb-6">
              — Or skip the form
            </div>

            <ul className="space-y-3">
              <li>
                <a
                  href={SITE.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-direct-whatsapp"
                  className="arrow-link group flex items-start gap-4 rounded-[16px] border border-white/[0.12] p-5 hover:border-white/25 transition-colors"
                >
                  <MessageCircle className="text-violet shrink-0 mt-0.5" size={20} />
                  <div className="flex-1">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-ink-muted">WhatsApp</div>
                    <div className="mt-1 text-lg tracking-tight text-white">+91 90678 20204</div>
                    <div className="mt-1 text-sm text-ink-muted">Fastest. Usually within hours.</div>
                  </div>
                  <ArrowUpRight className="arrow text-ink-muted group-hover:text-white" size={18} />
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${SITE.email}`}
                  data-testid="contact-direct-email"
                  className="arrow-link group flex items-start gap-4 rounded-[16px] border border-white/[0.12] p-5 hover:border-white/25 transition-colors"
                >
                  <Mail className="text-electric shrink-0 mt-0.5" size={20} />
                  <div className="flex-1">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-ink-muted">Email</div>
                    <div className="mt-1 text-lg tracking-tight text-white">{SITE.email}</div>
                    <div className="mt-1 text-sm text-ink-muted">RFPs, NDAs, longer briefs.</div>
                  </div>
                  <ArrowUpRight className="arrow text-ink-muted group-hover:text-white" size={18} />
                </a>
              </li>
              <li>
                <div
                  data-testid="contact-direct-location"
                  className="flex items-start gap-4 rounded-[16px] border border-white/[0.12] p-5"
                >
                  <MapPin className="text-ink-muted shrink-0 mt-0.5" size={20} />
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.22em] text-ink-muted">Studios</div>
                    <div className="mt-1 text-lg tracking-tight text-white">Pune · Bengaluru</div>
                    <div className="mt-1 text-sm text-ink-muted">Remote-first. IST working hours.</div>
                  </div>
                </div>
              </li>
            </ul>

            <div className="mt-8 rounded-[16px] border border-white/[0.12] bg-ink-bg p-5">
              <div className="text-[11px] uppercase tracking-[0.22em] text-ink-muted">— Capacity</div>
              <p className="mt-3 text-sm text-ink-muted leading-relaxed">
                We take on <span className="text-white">3 engagements per quarter</span>. If we’re booked
                we’ll tell you on day one and recommend a studio we trust.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CALENDAR EMBED */}
      <section
        data-testid="contact-calendar"
        className="relative border-t border-white/10 bg-ink-surface/30 py-20 md:py-28"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <SectionLabel num="02">Discovery call</SectionLabel>
            <h2 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight leading-[1.1]">
              Pick a slot. <span className="italic text-violet">Thirty minutes.</span>
            </h2>
            <p className="mt-5 text-ink-muted leading-relaxed max-w-sm">
              The call is with a partner—no SDR, no deck. Bring the URL of the product you wish yours
              behaved like, or just an honest problem statement.
            </p>
            <ul className="mt-6 space-y-2 text-sm">
              <li className="flex gap-3"><span className="text-violet">▸</span><span className="text-ink-muted">30 minutes · Google Meet</span></li>
              <li className="flex gap-3"><span className="text-violet">▸</span><span className="text-ink-muted">No NDA needed for the first call</span></li>
              <li className="flex gap-3"><span className="text-violet">▸</span><span className="text-ink-muted">A partner picks up — not a salesperson</span></li>
            </ul>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <MagneticButton
                href={SITE.whatsapp}
                external
                variant="ghost"
                data-testid="contact-calendar-whatsapp"
              >
                <MessageCircle size={15} />
                Or DM us first
              </MagneticButton>
            </div>
          </div>

          <Reveal className="md:col-span-8" delay={120}>
            <div className="relative rounded-[20px] border border-white/[0.12] bg-ink-bg overflow-hidden">
              {/* embed pane */}
              <div className="relative aspect-[4/3] md:aspect-[16/10]">
                <iframe
                  data-testid="contact-calendar-iframe"
                  title="Book a 30-minute discovery call with NioDelta"
                  src={CALENDAR_EMBED_URL}
                  className="absolute inset-0 h-full w-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allow="camera; microphone; clipboard-write"
                />
                {/* Quiet overlay strip with a fallback open-in-new-tab link */}
                <div className="absolute top-3 left-3 right-3 flex items-center justify-between gap-3 pointer-events-none">
                  <div className="pointer-events-auto inline-flex items-center gap-2.5 text-[10px] uppercase tracking-[0.28em] text-white/85 bg-black/40 backdrop-blur-sm border border-white/[0.12] rounded-full px-3 py-1.5">
                    <Calendar size={12} className="text-violet" />
                    NioDelta · 30 min discovery
                  </div>
                  <a
                    href={CALENDAR_EMBED_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-calendar-open"
                    className="pointer-events-auto inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-white/85 bg-black/40 backdrop-blur-sm border border-white/[0.12] rounded-full px-3 py-1.5 hover:bg-black/60"
                  >
                    Open in new tab <ArrowUpRight size={11} />
                  </a>
                </div>
              </div>
            </div>
            <p className="mt-3 text-[11px] uppercase tracking-[0.22em] text-ink-muted">
              Calendar runs on <code className="text-violet normal-case tracking-tight">cal.com/niodelta</code>. If the embed&apos;s slow, the WhatsApp DM lands faster.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Local animated gradient keyframes — kept page-local, calm pace */}
      <style>{`
        @keyframes nio-contact-glow {
          0%   { transform: translate3d(0,0,0) scale(1); }
          50%  { transform: translate3d(2%, -1%, 0) scale(1.04); }
          100% { transform: translate3d(0,0,0) scale(1); }
        }
        .contact-glow {
          animation: nio-contact-glow 14s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .contact-glow { animation: none; }
        }
      `}</style>
    </div>
  );
}

function Field({ label, className = '', children }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[11px] uppercase tracking-[0.22em] text-ink-muted mb-2.5">{label}</span>
      {children}
    </label>
  );
}
